import { listTags } from '@/features/atendimento/api';
import { ActionIconButton } from '@/shared/components/ui/ActionIconButton';
import { Button } from '@/shared/components/ui/Button';
import { useClientPagination } from '@/shared/hooks/useClientPagination';
import { useEscapeClose } from '@/shared/hooks/useEscapeClose';
import { confirm } from '@/shared/lib/confirm';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil, Plus, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { type TagRow, createTag, deleteTag, updateTag } from '../api';

export function TagsTab() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['tags'], queryFn: listTags });
  const [editing, setEditing] = useState<TagRow | 'new' | null>(null);
  const all = q.data ?? [];
  const { visible, hasMore, sentinelRef } = useClientPagination(all);

  const delMut = useMutation({
    mutationFn: deleteTag,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tags'] }),
  });

  return (
    <section className="flex h-full flex-col overflow-hidden">
      <header className="mb-5 flex flex-shrink-0 items-center justify-between">
        <div>
          <h2
            className="font-bold text-[20px] text-n-900"
            style={{ letterSpacing: '-.02em', marginBottom: '4px' }}
          >
            Etiquetas
          </h2>
          <p className="text-[13px] text-n-500">Categorize tickets e contatos.</p>
        </div>
        <Button onClick={() => setEditing('new')}>
          <Plus aria-hidden="true" />
          Nova etiqueta
        </Button>
      </header>

      {!q.isLoading && all.length > 0 && (
        <p className="mb-2 flex-shrink-0 text-[12px] text-n-500">
          {visible.length === all.length
            ? `${all.length} ${all.length === 1 ? 'etiqueta' : 'etiquetas'}`
            : `Mostrando ${visible.length} de ${all.length} etiquetas`}
        </p>
      )}
      <div className="min-h-0 flex flex-col overflow-y-auto rounded-md border border-n-200 bg-n-0 shadow-sm">
        <table className="w-full text-[12px]">
          <thead className="sticky top-0 z-10 border-b border-n-200 bg-n-50 text-left">
            <tr>
              <th className="px-[14px] py-[10px] text-[11px] font-bold uppercase tracking-[.05em] text-n-500 whitespace-nowrap">
                Etiqueta
              </th>
              <th className="w-[120px] px-[14px] py-[10px] text-[11px] font-bold uppercase tracking-[.05em] text-n-500 whitespace-nowrap">
                Escopo
              </th>
              <th className="w-[180px] px-[14px] py-[10px] text-right text-[11px] font-bold uppercase tracking-[.05em] text-n-500 whitespace-nowrap">
                Ações
              </th>
            </tr>
          </thead>
          <tbody>
            {q.isLoading && (
              <tr>
                <td colSpan={3} className="px-3 py-8 text-center">
                  <Loader2 size={18} className="mx-auto animate-spin text-n-400" />
                </td>
              </tr>
            )}
            {!q.isLoading && all.length === 0 && (
              <tr>
                <td colSpan={3} className="px-3 py-12 text-center text-n-500">
                  Nenhuma etiqueta cadastrada.
                </td>
              </tr>
            )}
            {visible.map((t) => (
              <tr key={t.id} className="border-b border-n-100 last:border-b-0 hover:bg-n-50">
                <td className="px-[14px] py-[11px]">
                  <span
                    className="rounded-pill px-2.5 py-1 font-semibold text-[11px]"
                    style={{ background: `${t.color}1f`, color: t.color }}
                  >
                    {t.name}
                  </span>
                </td>
                <td className="px-[14px] py-[11px] text-[12px] capitalize text-n-700">{t.scope}</td>
                <td className="px-[14px] py-[11px]">
                  <div className="flex justify-end gap-1">
                    <ActionIconButton
                      icon={Pencil}
                      label="Editar"
                      tone="brand"
                      onClick={() => setEditing(t)}
                    />
                    <ActionIconButton
                      icon={Trash2}
                      label="Apagar"
                      tone="danger"
                      onClick={async () => {
                        const ok = await confirm({
                          title: `Apagar "${t.name}"?`,
                          confirmText: 'Apagar',
                          variant: 'danger',
                        });
                        if (ok) delMut.mutate(t.id);
                      }}
                    />
                  </div>
                </td>
              </tr>
            ))}
            {hasMore && (
              <tr ref={sentinelRef}>
                <td colSpan={3} className="py-4 text-center text-[12px] text-n-500">
                  <span className="inline-flex items-center gap-2">
                    <Loader2 size={14} className="animate-spin" /> Carregando mais…
                  </span>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <TagFormModal tag={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
      )}
    </section>
  );
}

function TagFormModal({ tag, onClose }: { tag: TagRow | null; onClose: () => void }) {
  useEscapeClose(true, onClose);
  const qc = useQueryClient();
  const [name, setName] = useState(tag?.name ?? '');
  const [color, setColor] = useState(tag?.color ?? '#0A4EE3');
  const [scope, setScope] = useState<'ticket' | 'contact' | 'both'>(tag?.scope ?? 'ticket');
  const mut = useMutation({
    mutationFn: async () => {
      if (tag) await updateTag(tag.id, { name, color, scope });
      else await createTag({ name, color, scope });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tags'] });
      void qc.invalidateQueries({ queryKey: ['tags-contacts'] });
      onClose();
    },
  });
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-[380px] rounded-lg bg-n-0 p-6 shadow-lg">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-[16px]">{tag ? 'Editar etiqueta' : 'Nova etiqueta'}</h3>
          <button type="button" onClick={onClose} className="text-n-500" aria-label="Fechar">
            <X size={16} />
          </button>
        </div>
        <div className="mt-4 flex flex-col gap-3">
          <label className="flex flex-col gap-1">
            <span className="font-semibold text-[12px] text-n-700">Nome</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="rounded-md border border-n-200 px-3 py-2 text-[13px]"
              autoFocus
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="font-semibold text-[12px] text-n-700">Cor</span>
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="h-10 w-20 rounded-md border border-n-200"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="font-semibold text-[12px] text-n-700">Escopo</span>
            <select
              value={scope}
              onChange={(e) => setScope(e.target.value as 'ticket' | 'contact' | 'both')}
              className="rounded-md border border-n-200 px-3 py-2 text-[13px]"
            >
              <option value="ticket">Tickets</option>
              <option value="contact">Contatos</option>
              <option value="both">Ambos</option>
            </select>
          </label>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={() => mut.mutate()} disabled={mut.isPending || !name.trim()}>
            {mut.isPending && <Loader2 size={12} className="animate-spin" />}
            Salvar
          </Button>
        </div>
      </div>
    </div>
  );
}
